import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { getOfficerLabel } from '@/utils/legacy-utils';
import { getOfficerPluralLabel } from '@/utils/auth';
import { CompanyLink as CompanyLinkEntity } from '@/models/legacy/index';

// ── Types ────────────────────────────────────────────────────────────

interface CompanyLink {
  id: number;
  name: string;
  status: number; // 1 == pending, 2 == active
}

// ── Component ────────────────────────────────────────────────────────

export function CompanyLinkScreen() {
  const { theme } = useTheme();

  const [dispatcherCompanyList, setDispatcherCompanyList] = useState<CompanyLink[]>([]);
  const [officerCompanyList, setOfficerCompanyList] = useState<CompanyLink[]>([]);
  const [newLink, setNewLink] = useState('');
  const [officerLabel] = useState(getOfficerLabel());
  const [officerPluralLabel] = useState(getOfficerPluralLabel());

  // Terms Modal
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  // Remove Confirm Modal
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [removeConfirmMessage, setRemoveConfirmMessage] = useState('');
  const [removeConfirmLink, setRemoveConfirmLink] = useState<CompanyLink | null>(null);
  const [removeConfirmCompanyType, setRemoveConfirmCompanyType] = useState('');
  const [isRemoving, setIsRemoving] = useState<boolean>(false);

  const refreshCompanyLinks = useCallback(() => {
    api.getCompanyLinkList(true, (result: any) => {
      const dispatchers: CompanyLink[] = [];
      const officers: CompanyLink[] = [];

      for (let i = 0; i < result.dispatcherCompanyList.length; i++) {
        dispatchers.push(new CompanyLinkEntity(result.dispatcherCompanyList[i]));
      }
      for (let i = 0; i < result.officerCompanyList.length; i++) {
        officers.push(new CompanyLinkEntity(result.officerCompanyList[i]));
      }

      setDispatcherCompanyList(dispatchers);
      setOfficerCompanyList(officers);
    });
  }, []);

  // Initial load
  useEffect(() => {
    refreshCompanyLinks();
  }, [refreshCompanyLinks]);

  const addCompanyLink = useCallback(() => {
    const username = newLink?.trim();
    if (!username) {
      App.showPopupNotification('Please enter a username', 'warning');
      return;
    }

    api.addCompanyLink(username, false, () => {
      App.showPopupNotification('Company Link Pending', 'success');
      setNewLink('');
      refreshCompanyLinks();
    });
  }, [newLink, refreshCompanyLinks]);

  const acceptLink = useCallback((link: CompanyLink) => {
    api.acceptCompanyLink(link.id, 'dispatcher', false, () => {
      App.showPopupNotification('Company Link Accepted', 'success');
      refreshCompanyLinks();
    });
  }, [refreshCompanyLinks]);

  const promptRemoveLink = useCallback((link: CompanyLink, companyType: string) => {
    setRemoveConfirmLink(link);
    setRemoveConfirmCompanyType(companyType);
    setRemoveConfirmMessage(`Are you sure you want to remove the link with "${link.name}"?`);
    setRemoveConfirmOpen(true);
  }, []);

  const confirmRemoveLink = useCallback(() => {
    if (!removeConfirmLink) return;

    setIsRemoving(true);
    api.removeCompanyLink(removeConfirmLink.id, removeConfirmCompanyType, false, () => {
      setIsRemoving(false);
      App.showPopupNotification('Company Link Removed', 'success');
      refreshCompanyLinks();
      setRemoveConfirmOpen(false);
      setRemoveConfirmLink(null);
      setRemoveConfirmCompanyType('');
    });
  }, [removeConfirmLink, removeConfirmCompanyType, refreshCompanyLinks]);

  const cancelRemoveLink = useCallback(() => {
    setRemoveConfirmOpen(false);
    setRemoveConfirmLink(null);
    setRemoveConfirmCompanyType('');
  }, []);

  return (
    <div id="CompanyLinkSection" className={theme}>
      {/* Terms and Conditions Modal */}
      <ConfirmModal
        open={termsModalOpen}
        title="Company Link Terms and Conditions"
        dialogClass="kstc-dialog--wide"
        cancelText="Close"
        showCancel={true}
        onCancel={() => setTermsModalOpen(false)}
      >
        <div className="kst-terms-content">
          <p>In addition to the terms and conditions identified on <a href="//kerkton.com/terms" target="_blank">Terms and Conditions</a> you agree to the following Company Link Terms and Conditions linking with other companies:</p>
          <h4>Liability</h4>
          <ol>
            <li>By using company link, Kerkton Security Technologies LLC (Kerkton) is not responsible for how either party uses the data made available to them nor is Kerkton responsible for administrating the approval or removal of any link made between two parties.</li>
            <li>Parties involved in sharing of information will use all appropriate methods in order to prevent unauthorized access of that data or the unauthorized release of data.</li>
            <li>Parties involved have a responsibility for any data that they are linked with, any disclosure of information affecting another company or its personnel shall be notified prior to the release of information or within a reasonable time frame.</li>
            <li>Parties obtaining access to other party data, shall exercise at least the same degree of care as it uses with its own data and confidential information, but in no event less than reasonable care, to protect the data from misuse and unauthorized access or disclosure.</li>
            <li>Kerkton is not responsible for any agreements made between two parties creating a link or agreement.</li>
          </ol>
        </div>
      </ConfirmModal>

      {/* Remove Confirmation Modal */}
      <ConfirmModal
        open={removeConfirmOpen}
        title="Remove Company Link"
        message={removeConfirmMessage}
        confirmText="Remove"
        confirmDisabled={isRemoving}
        busy={isRemoving}
        cancelText="Cancel"
        autoFocus="cancel"
        scrollLock={true}
        onConfirm={confirmRemoveLink}
        onCancel={cancelRemoveLink}
      />

      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Company Link Page */}
            <div className="kst-page">
              <div className="kst-page-header">
                <div className="kst-page-header-content">
                  <h3 className="kst-page-title">Company Link</h3>
                  <p className="kst-page-description">
                    Link with other companies to dispatch calls to their {officerPluralLabel.toLowerCase()}.
                  </p>
                </div>
                <div className="kst-page-header-actions">
                  <button type="button" className="kstc-btn kstc-btn--secondary" onClick={() => setTermsModalOpen(true)}>
                    Terms and Conditions
                  </button>
                </div>
              </div>

              {/* Add New Link Section */}
              <div className="kst-link-add-section">
                <div className="kst-link-add-form">
                  <div className="kst-form-group">
                    <label className="kstc-label" htmlFor="newLinkUsername">Company Username</label>
                    <div className="kst-link-input-row">
                      <input
                        type="text"
                        id="newLinkUsername"
                        className="kstc-input"
                        value={newLink}
                        onChange={(e) => setNewLink(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') addCompanyLink(); }}
                        placeholder="Enter a username from the company you want to link to"
                      />
                      <button
                        type="button"
                        className="kstc-btn kstc-btn--primary"
                        onClick={addCompanyLink}
                        disabled={!newLink || !newLink.trim()}
                      >
                        Submit Request
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Link Lists */}
              <div className="kst-link-grid">
                {/* Dispatcher Company Links */}
                <div className="kst-link-panel">
                  <h4 className="kst-link-panel-title">Dispatcher Company Links</h4>
                  <div className="kst-link-panel-body">
                    {dispatcherCompanyList.length === 0 && (
                      <div className="kst-link-empty">
                        No dispatcher company links
                      </div>
                    )}
                    {dispatcherCompanyList.map((link) => (
                      <div key={link.id} className="kst-link-card">
                        <div className="kst-link-card-info">
                          <span className="kst-link-card-name">{link.name}</span>
                          {link.status === 1 ? (
                            <span className="kst-link-badge kst-link-badge--pending">Pending</span>
                          ) : (
                            <span className="kst-link-badge kst-link-badge--active">Active</span>
                          )}
                        </div>
                        <div className="kst-link-card-actions">
                          {link.status === 1 && (
                            <button
                              type="button"
                              className="kstc-btn kstc-btn--primary kstc-btn--sm"
                              onClick={() => acceptLink(link)}
                            >
                              Accept
                            </button>
                          )}
                          <button
                            type="button"
                            className="kstc-btn kstc-btn--secondary kstc-btn--sm"
                            onClick={() => promptRemoveLink(link, 'dispatcher')}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Officer Company Links */}
                <div className="kst-link-panel">
                  <h4 className="kst-link-panel-title">{officerLabel} Company Links</h4>
                  <div className="kst-link-panel-body">
                    {officerCompanyList.length === 0 && (
                      <div className="kst-link-empty">
                        No {officerLabel.toLowerCase()} company links
                      </div>
                    )}
                    {officerCompanyList.map((link) => (
                      <div key={link.id} className="kst-link-card">
                        <div className="kst-link-card-info">
                          <span className="kst-link-card-name">{link.name}</span>
                          {link.status === 1 ? (
                            <span className="kst-link-badge kst-link-badge--pending">Pending</span>
                          ) : (
                            <span className="kst-link-badge kst-link-badge--active">Active</span>
                          )}
                        </div>
                        <div className="kst-link-card-actions">
                          <button
                            type="button"
                            className="kstc-btn kstc-btn--secondary kstc-btn--sm"
                            onClick={() => promptRemoveLink(link, 'officer')}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
